import { CartUpdateEvent } from '@theme/events';

const config = document.querySelector('[data-hh-production-config]');
if (config) {
  const variantId = String(config.dataset.variantId || '');
  const root = window.Shopify?.routes?.root || '/';
  const storageKey = 'hh-production-pending-v1';
  let busy = false;
  let failed = false;
  let needsSync = false;
  let timer;
  let reconcileAfter = false;
  let pendingChoice = null;
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
    if (saved && Date.now() - saved.time < 1800000 && ['priority', 'standard'].includes(saved.choice)) pendingChoice = saved.choice;
    else window.sessionStorage.removeItem(storageKey);
  } catch {}

  const sections = () => [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const feeItems = cart => cart.items.filter(item => String(item.variant_id) === variantId || item.handle === 'bevorzugte-anfertigung');
  const hasGoods = cart => cart.items.some(item => item.requires_shipping && String(item.variant_id) !== variantId);
  const label = priority => priority ? config.dataset.priority : config.dataset.standard;

  function remember(choice) {
    pendingChoice = choice;
    try {
      if (choice === null) window.sessionStorage.removeItem(storageKey);
      else window.sessionStorage.setItem(storageKey, JSON.stringify({ choice, time: Date.now() }));
    } catch {}
  }

  async function request(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) throw new Error('cart_request_failed');
    return data;
  }

  function paint(choice) {
    document.querySelectorAll('[data-hh-production]').forEach(el => {
      el.dataset.selected = choice;
      el.querySelectorAll('[data-hh-production-option]').forEach(input => { input.checked = input.value === choice; });
    });
  }

  function setBusy(value) {
    busy = value;
    document.documentElement.classList.toggle('hh-production-busy', value || needsSync);
    document.querySelectorAll('[data-hh-production]').forEach(el => {
      el.setAttribute('aria-busy', String(value));
      el.querySelectorAll('input').forEach(input => { input.disabled = value; });
    });
    document.querySelectorAll('.cart__ctas').forEach(el => { el.inert = value || failed || needsSync; });
    document.querySelectorAll('[data-hh-production-status]').forEach(el => { el.textContent = value ? config.dataset.saving : ''; });
  }

  function setFailed(value) {
    failed = value;
    document.documentElement.classList.toggle('hh-production-error', value);
    document.querySelectorAll('[data-hh-production-error]').forEach(el => { el.hidden = !value; });
  }

  function render(cart, requestedSections) {
    if (requestedSections.some(id => !cart.sections?.[id])) {
      window.location.assign(root + 'cart');
      return;
    }
    document.dispatchEvent(new CartUpdateEvent(cart, 'hh-production', {
      source: 'hh-production', itemCount: cart.item_count, sections: cart.sections
    }));
  }

  async function update(cart, priority, requestedSections) {
    const updates = {};
    const fees = feeItems(cart);
    fees.forEach((item, index) => { updates[item.key] = priority && index === 0 ? 1 : 0; });
    if (priority && !fees.length) updates[variantId] = 1;
    const result = await request('cart/update.js', {
      updates,
      attributes: { Anfertigung: hasGoods(cart) ? label(priority) : '' },
      sections: requestedSections,
      sections_url: window.location.pathname
    });
    const resultFees = feeItems(result);
    if (priority ? resultFees.length !== 1 || resultFees[0].quantity !== 1 : resultFees.length !== 0) throw new Error('fee_not_confirmed');
    render(result, requestedSections);
    return result;
  }

  async function change(choice) {
    if (choice !== undefined) remember(choice);
    if (busy) { reconcileAfter = true; needsSync = true; return; }
    if (!variantId) return;
    clearTimeout(timer);
    needsSync = true;
    setBusy(true);
    const chosen = pendingChoice;
    try {
      const cart = await request('cart.js');
      const fees = feeItems(cart);
      const goods = hasGoods(cart);
      const desired = chosen === null ? fees.length > 0 : chosen === 'priority';
      const priority = goods && desired;
      const wrongFee = priority ? fees.length !== 1 || fees[0].quantity !== 1 : fees.length > 0;
      const expected = goods ? label(priority) : '';
      const staleAttribute = (cart.attributes?.Anfertigung || '') !== expected && (Boolean(cart.attributes?.Anfertigung) || (chosen !== null && goods) || priority);
      if (wrongFee || staleAttribute) await update(cart, priority, sections());
      if (goods && pendingChoice === chosen) remember(null);
      paint(pendingChoice || (priority ? 'priority' : 'standard'));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      const repeat = reconcileAfter;
      reconcileAfter = false;
      needsSync = repeat;
      setBusy(false);
      if (repeat) schedule();
    }
  }

  function schedule() {
    clearTimeout(timer);
    needsSync = true;
    document.querySelectorAll('.cart__ctas').forEach(el => { el.inert = true; });
    timer = setTimeout(() => change(), 150);
  }

  document.addEventListener('change', event => {
    if (event.target.matches('[data-hh-production-option]')) change(event.target.value);
  });
  document.addEventListener('cart:update', event => {
    if (event.detail?.data?.source !== 'hh-production') schedule();
  });
  document.addEventListener('click', event => {
    if ((busy || failed || needsSync) && event.target.closest('.cart__ctas')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', event => {
    if ((busy || failed || needsSync) && event.target.matches('form[action*="/cart"]') && !event.target.matches('form[action*="/cart/add"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('shopify:section:load', schedule);
  if (pendingChoice) paint(pendingChoice);
  if (document.querySelector('cart-items-component') || document.querySelector('[data-hh-production-product]')) schedule();
  window.addEventListener('pageshow', event => { if (event.persisted) schedule(); });
}

